import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom, map } from 'rxjs';
import { V3ImageCropDialogComponent } from '../../components-v3/image-crop-dialog/v3-image-crop-dialog.component';
import { V3ImageUploadDialogComponent } from '../../components-v3/image-upload-dialog/v3-image-upload-dialog.component';
import { V3ImageUploadDialogData } from '../../components-v3/image-upload-dialog/v3-image-upload-dialog.model';
import { V3LinkDialogComponent } from '../../components-v3/link-dialog/v3-link-dialog.component';
import { ExternalContentSummary } from '../../models';
import { ImageCropperData, ImageCropResult } from '../../models/images/image-crop.model';
import {
  ConfirmationPopupComponent,
  ConfirmationPopupData,
  ConfirmationPopupResult,
} from '../../styleguide/components/confirmation-popup/confirmation-popup.component';
import { LanguageCodeHelper } from '../language-code-helper';

/**
 * - `default`: Standard confirmation with a primary blue confirm button and equal-width actions.
 * - `destructive`: For irreversible actions (e.g. delete, archive). Confirm button is rendered
 * in alert red and takes up 70% of the actions row to draw attention to the risk.
 */
export type ConfirmDialogStyle = 'default' | 'destructive';

export interface V3ConfirmDialogConfig {
  message?: string;
  title?: string | HTMLElement;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonType?: 'primary' | 'secondary' | 'secondary-neutral';
  cancelButtonType?: 'primary' | 'secondary' | 'secondary-neutral';
  style?: ConfirmDialogStyle;
}

export interface V3InfoDialogConfig {
  title: string | HTMLElement;
  message?: string;
  confirmButtonText?: string;
}

@Injectable({
  providedIn: 'root',
})
export class V3DialogService {
  private dialog = inject(MatDialog);
  private transloco = inject(TranslocoService);

  showConfirmDialog(config: V3ConfirmDialogConfig): Promise<boolean> {
    const dialogRef = this.openConfirmationPopup({
      title: config.title || this.transloco.translate('translations.global.button.confirm'),
      message: config.message || '',
      cancelButtonText: config.cancelButtonText || this.transloco.translate('translations.global.button.cancel'),
      confirmButtonText: config.confirmButtonText || this.transloco.translate('translations.global.button.confirm'),
      cancelButtonType: config.cancelButtonType || 'secondary-neutral',
      confirmButtonType: config.confirmButtonType || (config.style === 'destructive' ? 'primary-alert' : 'primary'),
      style: config.style,
    });

    return firstValueFrom(dialogRef.afterClosed().pipe(map((result: ConfirmationPopupResult | undefined) => result?.confirmed ?? false)));
  }

  showInfoDialog(config: V3InfoDialogConfig): Promise<void> {
    const dialogRef = this.openConfirmationPopup({
      title: config.title,
      message: config.message || '',
      confirmButtonText: config.confirmButtonText || this.transloco.translate('translations.global.button.ok'),
      confirmButtonType: 'primary',
      hideCancelButton: true,
    });

    return firstValueFrom(dialogRef.afterClosed()).then(() => undefined);
  }

  private openConfirmationPopup(data: ConfirmationPopupData) {
    return this.dialog.open(ConfirmationPopupComponent, {
      width: '440px',
      maxWidth: '95vw',
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: 'ptl-v3-dialog',
      backdropClass: 'ptl-v3-dialog-backdrop-default',
      autoFocus: false,
      data,
    });
  }

  openImageUploadDialog(data: V3ImageUploadDialogData = {}): MatDialogRef<V3ImageUploadDialogComponent, ImageCropResult> {
    return this.dialog.open<V3ImageUploadDialogComponent, V3ImageUploadDialogData, ImageCropResult>(V3ImageUploadDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: 'ptl-v3-dialog',
      backdropClass: 'ptl-v3-dialog-backdrop-default',
      autoFocus: false,
      data,
    });
  }

  openLinkDialog(): MatDialogRef<V3LinkDialogComponent, ExternalContentSummary> {
    return this.dialog.open<V3LinkDialogComponent, void, ExternalContentSummary>(V3LinkDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: 'ptl-v3-dialog',
      backdropClass: 'ptl-v3-dialog-backdrop-default',
      autoFocus: 'input',
    });
  }

  showImageCropDialog(data: ImageCropperData): Promise<ImageCropResult | undefined> {
    const dialogRef = this.dialog.open<V3ImageCropDialogComponent, ImageCropperData, ImageCropResult>(V3ImageCropDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: 'ptl-v3-dialog',
      backdropClass: 'ptl-v3-dialog-backdrop-default',
      autoFocus: false,
      data,
    });

    return firstValueFrom(dialogRef.afterClosed());
  }
}
